import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchDepartments} from '@/entities/department/api';
import {DepartmentForm} from '@/features/manage-departments/ui/DepartmentForm';
import {requestControl} from '@/shared/api';
import {Icon} from '@/shared/ui/icons';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * A saved person's `department_ids` are sv_control ids, but the chips below are the
 * ORGANIZATION departments (control keeps a projection of them, keyed by
 * organization_department_id). Comparing the two id spaces directly matches nothing, so
 * reopening a person showed every chip unselected even though the link existed. This
 * list is the correlation table: control id → org id.
 */
type ControlDepartment = {
  department_id: string;
  organization_department_id?: string | null;
};
type ControlDepartments = {departments: ControlDepartment[]};

/**
 * Multi-select of the company's departments, as toggle chips. A person can belong to
 * several, so this is a set, not a radio. Saving a person needs at least one department
 * server-side, so the form treats an empty selection as invalid — the picker itself
 * just reports the chosen ids.
 *
 * The trailing "+" chip creates a department inline. Without it a company with no
 * departments yet is a dead end: the person cannot be saved, and leaving for the
 * Departments tab discards everything typed into the form (including a face photo).
 * The chip opens the same DepartmentForm the Departments tab uses — one definition of
 * what a department is — and selects whatever it creates, so the form becomes valid
 * without another tap.
 */
const styles = StyleSheet.create({
  chips: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    borderWidth: 1,
  },
  // Dashed to read as "add", not as another department you could select.
  addChip: {borderStyle: 'dashed'},
  empty: {gap: spacing(2)},
});

export function DepartmentPicker({
  companyId,
  value,
  onChange,
}: {
  companyId: string;
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['departments', companyId],
    queryFn: () => fetchDepartments(companyId, {perPage: 100}),
  });

  const controlQuery = useQuery({
    queryKey: ['control-departments', companyId],
    queryFn: () =>
      requestControl<ControlDepartments>('/control/departments', companyId),
  });

  // Selection always works in ORG ids: that is what the chips carry, and sv_control
  // accepts either id space on write (personnel_repo resolves department_id OR
  // organization_department_id), so emitting org ids on save stays correct.
  const selected = React.useMemo(() => {
    const toOrg = new Map<string, string>();
    for (const d of controlQuery.data?.departments ?? []) {
      if (d.organization_department_id) {
        toOrg.set(d.department_id, d.organization_department_id);
      }
    }
    return value.map(id => toOrg.get(id) ?? id);
  }, [value, controlQuery.data]);

  const toggle = (id: string) => {
    onChange(
      selected.includes(id) ? selected.filter(v => v !== id) : [...selected, id],
    );
  };

  const addChip = (
    <Pressable
      onPress={() => setCreating(true)}
      accessibilityRole="button"
      accessibilityLabel={t('department.createTitle')}
      style={[styles.chip, styles.addChip, {borderColor: p.primary}]}>
      <Icon name="plus" size={13} color={p.primary} />
      <AppText variant="label" color={p.primary}>
        {t('department.createTitle')}
      </AppText>
    </Pressable>
  );

  const modal = (
    <DepartmentForm
      companyId={companyId}
      visible={creating}
      onClose={() => setCreating(false)}
      onCreated={created => onChange([...selected, created.id])}
    />
  );

  // Wait for the correlation list too when something is already selected, otherwise the
  // chips render unselected for a frame and read as "the department was lost".
  if (query.isPending || (value.length > 0 && controlQuery.isPending)) {
    return <Skeleton height={38} radius={radii.md} width="70%" />;
  }

  const departments = query.data?.data ?? [];

  if (departments.length === 0) {
    return (
      <View style={styles.empty}>
        <AppText variant="caption">{t('personnel.noDepartments')}</AppText>
        <View style={styles.chips}>{addChip}</View>
        {modal}
      </View>
    );
  }

  return (
    <View style={styles.chips}>
      {departments.map(d => {
        const active = selected.includes(d.id);
        return (
          <Pressable
            key={d.id}
            onPress={() => toggle(d.id)}
            accessibilityRole="button"
            accessibilityState={{selected: active}}
            style={({pressed}) => [
              styles.chip,
              {
                backgroundColor: active ? p.accent : pressed ? p.muted : p.card,
                borderColor: active ? p.primary : p.border,
              },
            ]}>
            {active ? <Icon name="check" size={12} color={p.accentForeground} /> : null}
            <AppText variant="label" color={active ? p.accentForeground : p.foreground}>
              {d.name}
            </AppText>
          </Pressable>
        );
      })}
      {addChip}
      {modal}
    </View>
  );
}
